import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

import { getImagePath } from '../config/imagePath';

// 1. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
  NO_CANT_IDS,
} from '../config/globalSettings';

// 2. Import Data
import { holsterData } from '../data/owbdata';
import '../App.css';

function OWBBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [isLightbearing, setIsLightbearing] = useState("no"); 
  const [lightId, setLightId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  
  // Mag Carrier State
  const [magId, setMagId] = useState("");
  const [magMountId, setMagMountId] = useState(""); // Tracks the mount for the mag carrier

  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // New State Variables
  const [opticId, setOpticId] = useState("yes"); 
  const [cantId, setCantId] = useState("none");

  // Sub-Options State (Added for Nidhogg Parity)
  const [tacBeltColor, setTacBeltColor] = useState("");
  const [tacBeltSize, setTacBeltSize] = useState("");
  const [fulcrumColor, setFulcrumColor] = useState("");
  const [thighBandColor, setThighBandColor] = useState("");

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, 
    magId, magMountId, breakthroughId, rushService,
    opticId, cantId,
    tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor
  });

  // --- SAFE LOAD CONFIG ---
  const loadConfig = (incomingData) => {
    if (!incomingData || incomingData.type === 'click' || incomingData._reactName) return;

    try {
        const config = incomingData.config ? incomingData.config : incomingData;

        setHandId(config.handId || 'right');
        setBrandId(config.brandId || '');
        setGunId(config.gunId || '');
        
        const safeLightbearing = config.isLightbearing || 'no';
        setIsLightbearing(safeLightbearing);
        
        if (safeLightbearing === 'yes') {
            setLightId(config.lightId || '');
        } else {
            setLightId('');
        }

        setColorId(config.colorId || '');
        setMountId(config.mountId || '');
        
        // Mag carrier load
        setMagId(config.magId || '');
        setMagMountId(config.magMountId || '');

        setBreakthroughId(config.breakthroughId || '');
        setRushService(config.rushService === true);
        
        setOpticId(config.opticId || 'yes');
        setCantId(config.cantId || 'none');

        setTacBeltColor(config.tacBeltColor || "");
        setTacBeltSize(config.tacBeltSize || "");
        setFulcrumColor(config.fulcrumColor || "");
        setThighBandColor(config.thighBandColor || "");

        setShareModalOpen(false);
    } catch (error) {
        console.error("Error loading config", error);
    }
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, magId, magMountId, breakthroughId, rushService, opticId, cantId, tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor]);

  // Handle resetting sub-options if mount changes
  useEffect(() => {
    const needsTacBeltOptions = 
        mountId === 'tac_belt_standard' || 
        mountId === 'dropleg_qls' || 
        mountId === 'dropleg_no_qls';

    if (!needsTacBeltOptions) {
        setTacBeltColor("");
        setTacBeltSize("");
    }

    const needsDroplegOptions = 
        mountId === 'dropleg_qls' || 
        mountId === 'dropleg_no_qls';

    if (!needsDroplegOptions) {
        setFulcrumColor("");
        setThighBandColor("");
    }
  }, [mountId]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING ---
  const availableGuns = brandId 
    ? holsterData.guns.filter(g => {
        if (g.brand_id !== brandId) return false;
        if (handId === "left" && g.right_hand_only) return false;
        return true;
      })
    : [];

  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);
  
  const selectedMag = holsterData.mag_carriers?.find(m => m.id === magId);
  const selectedMagMount = holsterData.mag_mounts?.find(m => m.id === magMountId);
  
  const selectedOptic = holsterData.optic_options?.find(o => o.id === opticId);
  const selectedCant = holsterData.cant_options?.find(c => c.id === cantId);

  // Sub-option Lookups
  const selectedTacColor = holsterData.tac_belt_colors ? holsterData.tac_belt_colors.find(c => c.id === tacBeltColor) : null;
  const selectedTacSize = holsterData.tac_belt_sizes ? holsterData.tac_belt_sizes.find(s => s.id === tacBeltSize) : null;
  const selectedFulcrum = holsterData.fulcrum_colors ? holsterData.fulcrum_colors.find(c => c.id === fulcrumColor) : null;
  const selectedThigh = holsterData.thigh_band_colors ? holsterData.thigh_band_colors.find(c => c.id === thighBandColor) : null;

  // --- CONDITIONAL LOGIC FOR MAG CARRIER MOUNT ---
  const showMagMount = magId !== "" && magId !== "no";

  useEffect(() => {
      if (!showMagMount) {
          setMagMountId("");
      }
  }, [showMagMount]);

  // --- DYNAMIC MAG MOUNT MAPPING FOR DROPDOWN UI ---
  const dynamicMagMounts = (holsterData.mag_mounts || []).map(mount => ({
      ...mount,
      name: magId === "yes_2" ? `2x ${mount.name}` : mount.name,
      price: magId === "yes_2" ? mount.price * 2 : mount.price
  }));

  // --- DYNAMIC LIGHTBEARING LOGIC ---
  let supportedLightsArray = selectedGun?.supported_lights || [];
  
  if (handId === 'left' && selectedGun?.supported_lights_lh) {
      supportedLightsArray = selectedGun.supported_lights_lh;
  }

  const supportsLights = selectedGun && supportedLightsArray.length > 0;

  // --- AUTO-RESET ---
  useEffect(() => {
    if (selectedGun) {
      if (!supportsLights) {
        setIsLightbearing("no"); 
        setLightId("");          
      }
    }
  }, [gunId, selectedGun, supportsLights]);

  // --- LIGHT FILTERING ---
  const availableLights = isLightbearing === "yes" && selectedGun
    ? holsterData.lights.filter(light => supportedLightsArray.includes(light.id))
    : [];

  const selectedLight = holsterData.lights.find(l => l.id === lightId);

  // Reset Light if invalid
  useEffect(() => {
    if (lightId && !availableLights.find(l => l.id === lightId)) {
        setLightId("");
    }
  }, [handId, gunId, availableLights, lightId]);


  // --- LEAD TIME LOGIC (PULLED FROM GLOBAL SETTINGS) ---
  const calculateLeadTime = () => {
    if (!selectedGun || !handId) return { text: "Select Options", date: "...", type: "PENDING" };
    if (isLightbearing === "yes" && !selectedLight) return { text: "Select Light", date: "...", type: "PENDING" };

    let isQuickShip = false;

    if (isLightbearing === "no") {
        if (handId === "right" && selectedGun.qs_nlb_rh) isQuickShip = true;
        if (handId === "left" && selectedGun.qs_nlb_lh) isQuickShip = true;
    } 
    else if (isLightbearing === "yes" && selectedLight) {
        if (handId === "right" && Array.isArray(selectedGun.qs_lb_rh) && selectedGun.qs_lb_rh.includes(lightId)) isQuickShip = true;
        if (handId === "left" && Array.isArray(selectedGun.qs_lb_lh) && selectedGun.qs_lb_lh.includes(lightId)) isQuickShip = true;
    }

    const qsDays = QS_DAYS;       
    const rushDays = RUSH_DAYS;    
    const customDays = CUSTOM_DAYS;  
    
    let targetDays = customDays;
    if (isQuickShip) {
        targetDays = qsDays;
    } else if (rushService) {
        targetDays = rushDays;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true };
    }
    return { type: CUSTOM_LABEL, timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
      if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
          setRushService(false);
      }
  }, [gunId, handId, lightId, isLightbearing]);

  // --- PRICING ---
  const BASE_PRICE = isLightbearing === "yes" ? 97.99 : 87.99;
  
  let activeMagMountPrice = 0;
  if (showMagMount && selectedMagMount) {
      activeMagMountPrice = magId === "yes_2" ? (selectedMagMount.price * 2) : selectedMagMount.price;
  }

  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedTacColor?.price || 0) +
    (selectedTacSize?.price || 0) +
    (selectedFulcrum?.price || 0) +
    (selectedThigh?.price || 0) +
    (selectedMag?.price || 0) +
    activeMagMountPrice +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // Validation
  let subOptionsValid = true;
  if (mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!tacBeltColor || !tacBeltSize) subOptionsValid = false;
  }
  if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!fulcrumColor || !thighBandColor) subOptionsValid = false;
  }

  let isReady = brandId && gunId && colorId && mountId && magId && breakthroughId && cantId && subOptionsValid &&
    (isLightbearing === "no" || (isLightbearing === "yes" && lightId));

  if (showMagMount && !magMountId) {
      isReady = false;
  }

  // --- ADD TO CART ---
  const handleAddToCart = () => {
    if (!isReady) return;

    let mountDetails = selectedMount?.name;
    if (mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        mountDetails += `\n    - Belt Mount Color: ${selectedTacColor?.name}`;
        mountDetails += `\n    - Belt Mount Size: ${selectedTacSize?.name}`;
    }
    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        mountDetails += `\n    - Fulcrum Color: ${selectedFulcrum?.name}`;
        mountDetails += `\n    - Thigh Band: ${selectedThigh?.name}`;
    }

    let magDetails = null;
    if (showMagMount) {
        const mountNameStr = magId === "yes_2" ? `2x ${selectedMagMount?.name}` : selectedMagMount?.name;
        magDetails = `Mag Carrier: ${selectedMag?.name}\n    - Mag Mount: ${mountNameStr}`;
    } else {
        // Declined: leave it off the ticket entirely.
        magDetails = null;
    }

    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        isLightbearing === "yes" ? `Weapon Light: ${selectedLight?.name}` : "Lightbearing: No",
        `Color: ${selectedColor?.name}`,
        `Mount: ${mountDetails}`,
        isLightbearing === "yes" ? `Retention: Standard Fixed` : `Retention: Standard Adjustable`,
        `Optic Cut: ${selectedOptic?.name}`,
        NO_CANT_IDS.includes(cantId) ? null : `Cant: ${selectedCant?.name}`,
        magDetails,
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283903; 
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration); 
    params.append('anr_price', total);          

    const addToCartURL = `/checkout/?${params.toString()}`;
    window.location.href = addToCartURL;
  };

  const getSystemImages = () => {
    let images = [];

    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        const suffix = mountId === 'dropleg_no_qls' ? '_no_qls' : '';
        
        if (tacBeltColor) {
            images.push(getImagePath(`mounts/owb_systems/tacbelt_${tacBeltColor}${suffix}_front.png`));
        }
        if (fulcrumColor) {
            const activeThigh = thighBandColor || "black";
            images.push(getImagePath(`mounts/owb_systems/dropleg_${fulcrumColor}_${activeThigh}${suffix}_front.png`));
        }
    }

    return images;
  };

  const systemImageArray = getSystemImages();
  const hasMountSystem = ['dropleg_qls', 'dropleg_no_qls'].includes(mountId);

  // --- MOUNT INTERCEPTOR ---
  let visualizerMountData = selectedMount;
  if (selectedMount && selectedMount.id === 'tac_belt_standard' && tacBeltColor) {
      visualizerMountData = {
          ...selectedMount,
          img_layer_mount: `/images/mounts/tac_belt_standard_${tacBeltColor}_front.png`,
          img_layer_mount_back: `/images/mounts/tac_belt_standard_${tacBeltColor}_back.png`
      };
  }

  const SectionHeader = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0 }}>{title}</h3>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">OWB Holster Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>OUTSIDE THE WAISTBAND CONFIGURATOR</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Secure and Discreet:</strong> Our OWB holsters hug your body for minimal printing and snag-free carry, a perfect addition to your IWB holster for concealed carry.</li>
             <li><strong style={{ color: "#fff" }}>Retention:</strong> All Non-Lightbearing models come standard with Adjustable Retention. Lightbearing models feature standard fixed retention.</li>
             <li><strong style={{ color: "#fff" }}>Full Trigger Guard Protection:</strong> Safeguard your firearm with complete trigger guard coverage for added safety.</li>
             <li><strong style={{ color: "#fff" }}>Built for Hard Use:</strong> Crafted from premium KYDEX® Thermoplastics, our holsters are built to withstand daily wear and tear.</li>
             <li><strong style={{ color: "#fff" }}>Optic and Suppressor Ready:</strong> Compatible with most optics and suppressor-height sights up to 0.450″.</li>
             <li><strong style={{ color: "#fff" }}>Threaded Barrel Ready:</strong> Designed to fit firearms with threaded barrels.</li>
           </ul>
        </div>

        {/* INPUTS */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={holsterData.hands} 
          selectedValue={handId} 
          onChange={(val) => { setHandId(val); setLightId(""); }} 
        />

        <OptionSelector label="Select Manufacturer *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Gun Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />
        
        {/* CONDITIONALLY SHOW LIGHTBEARING TOGGLE OR ERROR MESSAGE */}
        {(!selectedGun || supportsLights) && (
          <div className="fade-in-section" style={{marginTop: "15px"}}>
            <SegmentedControl 
                label="Weapon Light" 
                options={holsterData.lightbearing_options} 
                selectedValue={isLightbearing} 
                onChange={(val) => { setIsLightbearing(val); setLightId(""); }} 
            />
          </div>
        )}

        {selectedGun && !supportsLights && (
          <div style={{ marginTop: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#1e1e1e", border: "1px solid #444", borderRadius: "4px", fontSize: "14px", color: "#ccc" }}>
              <strong style={{color: "#D5A678", display: "block", marginBottom: "5px"}}>No Lightbearing Models Available</strong>
              <p style={{margin: "0 0 10px 0", fontSize: "13px"}}>We do not currently offer a standard lightbearing option for this specific firearm model.</p>
              <a href="https://www.anrkydexholsters.com/custom-holster-request/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678", textDecoration: "underline", fontSize: "13px", fontWeight: "bold"}}>Custom Holster Request | ANR Design Holsters</a>
          </div>
        )}

        {isLightbearing === "yes" && <OptionSelector label="Select Weapon Light *" options={availableLights} selectedValue={lightId} onChange={setLightId} />}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS */}
        <SectionHeader title="Color Options" description="ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation." />
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* Cant */}
        <SectionHeader 
          title="Cant" 
          description={
            <div>
              0° is a vertical draw. 10° tilts the grip forward to aid in concealment.<br /><br />
              <a href="https://helpcenter.anrkydexholsters.com/understanding-holster-cant/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                Learn more about holster cant
              </a>
            </div>
          } 
        />
        <OptionSelector label="Select Cant *" options={holsterData.cant_options} selectedValue={cantId} onChange={setCantId} />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Mounting Options</h3>
          <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content mega-tooltip">
                  {holsterData.mounts.map(mount => (
                      <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                          <div className="mount-text">
                              <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                              <p style={{margin: 0}}>{mount.description}</p>
                          </div>
                      </div>
                  ))}
                  <div style={{ textalign: "center", marginTop: "10px" }}>
                      <a href="https://helpcenter.anrkydexholsters.com/understanding-loop-options-for-our-traditional-owb-builders/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                          Learn more about loop options
                      </a>
                  </div>
              </div>
          </div>
        </div>
        <OptionSelector label="Select Mount *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* DROPDOWNS FOR CONDITIONAL MOUNTS */}
        {(mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Tactical Belt Mount Color" description="Select the color for your Tac Belt attachment." />
                <OptionSelector label="Select Color *" options={holsterData.tac_belt_colors} selectedValue={tacBeltColor} onChange={setTacBeltColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Tactical Belt Mount Size" description="Choose the size of your belt loop. Short drops ride higher on the belt." imageUrl="/wp-content/uploads/anr-assets/images/info/tac_belt_sizes.jpg" />
                <OptionSelector label="Select Size *" options={holsterData.tac_belt_sizes} selectedValue={tacBeltSize} onChange={setTacBeltSize} />
            </div>
        )}

        {(mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Fulcrum Color" description="Select the color for the pivoting fulcrum mechanism." imageUrl="/wp-content/uploads/anr-assets/images/info/fulcrum_colors.jpg" />
                <OptionSelector label="Select Fulcrum Color *" options={holsterData.fulcrum_colors} selectedValue={fulcrumColor} onChange={setFulcrumColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Thigh Band Color" description="Select the color for the elastic thigh strap." imageUrl="/wp-content/uploads/anr-assets/images/info/thigh_bands.jpg" />
                <OptionSelector label="Select Thigh Band *" options={holsterData.thigh_band_colors} selectedValue={thighBandColor} onChange={setThighBandColor} />
            </div>
        )}

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        {/* BREAKTHROUGH */}
        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>Optional firearm maintenance cleaning solvents.</p>
        <OptionSelector label="Optional firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        {/* MAG CARRIER SYSTEM */}
        {holsterData.mag_carriers && (
            <>
                <div style={{marginTop: "30px"}}></div>
                <SectionHeader title="Add Matching Mag Carrier" description="Standard Pistol Magazine Carrier, matching your holster color." />
                <OptionSelector label="Add a Magazine Carrier *" options={holsterData.mag_carriers} selectedValue={magId} onChange={setMagId} />
            </>
        )}

        {/* DYNAMIC MAG MOUNT */}
        {showMagMount && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <h4 style={{ color: "#D5A678", marginTop: "0", marginBottom: "0", fontSize: "14px", textTransform: "uppercase" }}>Select Mount for Magazine Carrier</h4>
                </div>
                <OptionSelector 
                    label="Mag Mount *" 
                    options={dynamicMagMounts} 
                    selectedValue={magMountId} 
                    onChange={setMagMountId} 
                />
            </div>
        )}

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* COLLAPSIBLE DESCRIPTION */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                The <strong>Standard OWB</strong> is a heavy-duty Outside the Waistband holster designed for open carry, range use, or duty. 
                Built from .080" Kydex for maximum durability.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      {/* PREVIEW SECTION */}
      <div className="preview-section">
        <div className="sticky-wrapper">
          <HolsterVisualizer 
            holsterType="owb" 
            gunData={selectedGun} 
            lightData={selectedLight}
            colorData={selectedColor} 
            mountData={visualizerMountData} 
            tacBeltColor={tacBeltColor}
            fulcrumColor={fulcrumColor}
            thighBandColor={thighBandColor}
            isLightbearing={isLightbearing} 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
            systemImage={systemImageArray}
            hasSystemView={hasMountSystem}
            mountBehindFrontShell={mountId === 'tac_belt_standard' || mountId === 'tac_belt_bolted'}
          />

          {/* FRONT / REAR TOGGLE */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* LEAD TIME BOX */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input type="checkbox" checked={rushService} onChange={(e) => setRushService(e.target.checked)} style={{ marginRight: "8px", accentColor: "#000" }} />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedGun?.name} colorName={selectedColor?.name} />
    </div>
  );
}

export default OWBBuilder;